import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useObrasContext } from '@/contexts/ObrasContext';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Plus, Package, Warehouse, MapPin, User, Calendar, Trash2, Eye, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { RecepcionMaterialDocumentos } from '@/components/recepciones/RecepcionMaterialDocumentos';

interface Recepcion {
  id: string;
  empresa_id: string;
  usuario_id: string;
  fecha_recepcion: string;
  obra_id: string | null;
  orden_trabajo_id: string | null;
  ubicacion: 'almacen' | 'obra';
  proveedor: string | null;
  notas: string | null;
  created_at: string;
  // joins
  obra?: { nombre: string } | null;
  orden_trabajo?: { numero_ot: string; titulo: string } | null;
  usuario?: { nombre: string } | null;
  doc_count?: number;
}

const NONE = '__none__';

export default function RecepcionesMaterial() {
  const { user, perfil } = useAuth();
  const { obras } = useObrasContext();
  const { ordenes } = useOrdenesTrabajoContext();
  const { toast } = useToast();

  const isOffice = perfil?.rol === 'admin' || perfil?.rol === 'gestor';

  const [recepciones, setRecepciones] = useState<Recepcion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);

  // Filtros
  const [filtroUbicacion, setFiltroUbicacion] = useState<string>('all');
  const [filtroObra, setFiltroObra] = useState<string>('all');
  const [filtroBusqueda, setFiltroBusqueda] = useState('');

  // Formulario
  const [form, setForm] = useState({
    ubicacion: 'almacen' as 'almacen' | 'obra',
    obra_id: NONE,
    orden_trabajo_id: NONE,
    proveedor: '',
    notas: '',
    numero_albaran: '',
    importe_total: '',
    iva_porcentaje: '21',
    fecha_recepcion: '',
  });
  const [creating, setCreating] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchRecepciones = async () => {
    setLoading(true);
    try {
      // Intento con joins embebidos
      let rows: any[] = [];
      const embedded = await (supabase.from('recepciones_material' as any) as any)
        .select(`
          *,
          obra:obras(nombre),
          orden_trabajo:ordenes_trabajo(numero_ot, titulo),
          usuario:perfiles!recepciones_material_usuario_id_fkey(nombre)
        `)
        .order('fecha_recepcion', { ascending: false });

      if (!embedded.error) {
        rows = embedded.data || [];
      } else {
        // Fallback sin joins embebidos: resolver relaciones manualmente
        console.warn('Embedded select failed, using manual joins:', embedded.error.message);
        const plain = await (supabase.from('recepciones_material' as any) as any)
          .select('*')
          .order('fecha_recepcion', { ascending: false });
        if (plain.error) throw plain.error;
        rows = plain.data || [];

        const obraIds = [...new Set(rows.map((r: any) => r.obra_id).filter(Boolean))];
        const otIds = [...new Set(rows.map((r: any) => r.orden_trabajo_id).filter(Boolean))];
        const userIds = [...new Set(rows.map((r: any) => r.usuario_id).filter(Boolean))];

        const [obrasRes, otsRes, perfsRes] = await Promise.all([
          obraIds.length
            ? supabase.from('obras').select('id, nombre').in('id', obraIds as any)
            : Promise.resolve({ data: [] as any[] }),
          otIds.length
            ? supabase.from('ordenes_trabajo').select('id, numero_ot, titulo').in('id', otIds as any)
            : Promise.resolve({ data: [] as any[] }),
          userIds.length
            ? supabase.from('perfiles').select('user_id, nombre').in('user_id', userIds as any)
            : Promise.resolve({ data: [] as any[] }),
        ]);
        const obrasMap = new Map((obrasRes.data || []).map((o: any) => [o.id, o]));
        const otsMap = new Map((otsRes.data || []).map((o: any) => [o.id, o]));
        const perfsMap = new Map((perfsRes.data || []).map((p: any) => [p.user_id, p.nombre]));

        rows = rows.map((r: any) => ({
          ...r,
          obra: r.obra_id ? { nombre: (obrasMap.get(r.obra_id) as any)?.nombre } : null,
          orden_trabajo: r.orden_trabajo_id
            ? {
                numero_ot: (otsMap.get(r.orden_trabajo_id) as any)?.numero_ot,
                titulo: (otsMap.get(r.orden_trabajo_id) as any)?.titulo,
              }
            : null,
          usuario: { nombre: perfsMap.get(r.usuario_id) || 'Usuario' },
        }));
      }

      // Contar documentos
      const ids = rows.map((r: any) => r.id);
      const docCounts = new Map<string, number>();
      if (ids.length > 0) {
        const { data: docs } = await (supabase.from('recepcion_material_documentos' as any) as any)
          .select('recepcion_id')
          .in('recepcion_id', ids);
        (docs || []).forEach((d: any) => {
          docCounts.set(d.recepcion_id, (docCounts.get(d.recepcion_id) || 0) + 1);
        });
      }

      setRecepciones(
        rows.map((r: any) => ({
          ...r,
          doc_count: docCounts.get(r.id) || 0,
        })),
      );
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error', description: e.message || 'No se pudieron cargar las recepciones', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchRecepciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = useMemo(() => {
    return recepciones.filter((r) => {
      if (filtroUbicacion !== 'all' && r.ubicacion !== filtroUbicacion) return false;
      if (filtroObra !== 'all' && r.obra_id !== filtroObra) return false;
      if (filtroBusqueda) {
        const q = filtroBusqueda.toLowerCase();
        const blob = `${r.proveedor || ''} ${r.notas || ''} ${r.obra?.nombre || ''} ${r.orden_trabajo?.numero_ot || ''} ${r.orden_trabajo?.titulo || ''} ${r.usuario?.nombre || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [recepciones, filtroUbicacion, filtroObra, filtroBusqueda]);

  const obrasActivas = useMemo(
    () => obras.filter((o: any) => o.estado === 'activa'),
    [obras],
  );

  const otsActivas = useMemo(
    () => ordenes.filter((o: any) => o.estado !== 'finalizada' && o.estado !== 'cancelada'),
    [ordenes],
  );

  const otsFiltradas = useMemo(() => {
    if (form.obra_id !== NONE) return otsActivas.filter((o: any) => o.obra_id === form.obra_id);
    return otsActivas;
  }, [otsActivas, form.obra_id]);

  const proveedoresExistentes = useMemo(() => {
    const set = new Set<string>();
    recepciones.forEach((r) => {
      const p = (r.proveedor || '').trim();
      if (p) set.add(p);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [recepciones]);

  const resetForm = () => {
    setForm({ ubicacion: 'almacen', obra_id: NONE, orden_trabajo_id: NONE, proveedor: '', notas: '', numero_albaran: '', importe_total: '', iva_porcentaje: '21', fecha_recepcion: '' });
    setCreatedId(null);
    setEditingId(null);
  };

  const openEdit = (r: Recepcion) => {
    const raw = r as any;
    setEditingId(r.id);
    setCreatedId(r.id); // skip the "create then add photos" flow; admin can manage docs in the detalle dialog
    setForm({
      ubicacion: r.ubicacion,
      obra_id: r.obra_id || NONE,
      orden_trabajo_id: r.orden_trabajo_id || NONE,
      proveedor: r.proveedor || '',
      notas: r.notas || '',
      numero_albaran: raw.numero_albaran || '',
      importe_total: raw.importe_total != null ? String(raw.importe_total) : '',
      iva_porcentaje: raw.iva_porcentaje != null ? String(raw.iva_porcentaje) : '21',
      fecha_recepcion: r.fecha_recepcion ? r.fecha_recepcion.slice(0, 16) : '',
    });
    setCreatedId(null);
    setShowForm(true);
  };

  const handleCreate = async () => {
    if (!user || !perfil?.empresa_id) return;
    setCreating(true);
    try {
      const importe = form.importe_total ? parseFloat(form.importe_total) : null;
      const basePayload: any = {
        ubicacion: form.ubicacion,
        obra_id: form.obra_id === NONE ? null : form.obra_id,
        orden_trabajo_id: form.orden_trabajo_id === NONE ? null : form.orden_trabajo_id,
        proveedor: form.proveedor.trim() || null,
        notas: form.notas.trim() || null,
        numero_albaran: form.numero_albaran.trim() || null,
        importe_total: importe,
        iva_porcentaje: importe != null && form.iva_porcentaje ? Number(form.iva_porcentaje) : null,
      };

      if (editingId) {
        const updatePayload: any = { ...basePayload };
        if (form.fecha_recepcion) {
          updatePayload.fecha_recepcion = new Date(form.fecha_recepcion).toISOString();
        }
        if (importe != null) {
          updatePayload.fecha_valoracion = new Date().toISOString();
          updatePayload.valorado_por = user.id;
        } else {
          updatePayload.fecha_valoracion = null;
          updatePayload.valorado_por = null;
        }
        const { error } = await (supabase.from('recepciones_material' as any) as any)
          .update(updatePayload)
          .eq('id', editingId);
        if (error) throw error;
        toast({ title: 'Recepción actualizada' });
        fetchRecepciones();
        handleCloseForm();
      } else {
        const payload: any = {
          ...basePayload,
          empresa_id: perfil.empresa_id,
          usuario_id: user.id,
          fecha_valoracion: importe != null ? new Date().toISOString() : null,
          valorado_por: importe != null ? user.id : null,
        };
        const { data, error } = await (supabase.from('recepciones_material' as any) as any)
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setCreatedId((data as any).id);
        toast({ title: 'Recepción creada', description: 'Ahora añade las fotos del albarán y los materiales' });
        fetchRecepciones();
      }
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error', description: e.message || 'Error al guardar', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleDelete = async (r: Recepcion) => {
    try {
      const { error } = await (supabase.from('recepciones_material' as any) as any).delete().eq('id', r.id);
      if (error) throw error;
      toast({ title: 'Recepción eliminada' });
      fetchRecepciones();
      if (detalleId === r.id) setDetalleId(null);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const detalle = detalleId ? recepciones.find((r) => r.id === detalleId) : null;

  return (
    <div className="container mx-auto p-4 space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6 text-orange-400" />
            Recepción de Materiales
          </h1>
          <p className="text-muted-foreground text-sm">
            Registra materiales recibidos en almacén o en obra
          </p>
        </div>
        <Dialog open={showForm} onOpenChange={(o) => (o ? setShowForm(true) : handleCloseForm())}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Nueva recepción
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar recepción de material' : 'Nueva recepción de material'}</DialogTitle>
            </DialogHeader>

            {!createdId ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Ubicación</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={form.ubicacion === 'almacen' ? 'default' : 'outline'}
                      onClick={() => setForm({ ...form, ubicacion: 'almacen' })}
                      className="h-12"
                    >
                      <Warehouse className="h-4 w-4 mr-2" /> Almacén
                    </Button>
                    <Button
                      type="button"
                      variant={form.ubicacion === 'obra' ? 'default' : 'outline'}
                      onClick={() => setForm({ ...form, ubicacion: 'obra' })}
                      className="h-12"
                    >
                      <MapPin className="h-4 w-4 mr-2" /> Obra
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Obra (opcional)</Label>
                  <Select
                    value={form.obra_id}
                    onValueChange={(v) => setForm({ ...form, obra_id: v, orden_trabajo_id: NONE })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Sin obra —</SelectItem>
                      {obrasActivas.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Orden de Trabajo (opcional)</Label>
                  <Select
                    value={form.orden_trabajo_id}
                    onValueChange={(v) => setForm({ ...form, orden_trabajo_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Sin OT —</SelectItem>
                      {otsFiltradas.map((o: any) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.numero_ot} — {o.titulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="proveedor">Proveedor (opcional)</Label>
                  <Input
                    id="proveedor"
                    list="proveedores-list"
                    value={form.proveedor}
                    onChange={(e) => setForm({ ...form, proveedor: e.target.value })}
                    placeholder="Selecciona o escribe uno nuevo…"
                    autoComplete="off"
                  />
                  <datalist id="proveedores-list">
                    {proveedoresExistentes.map((p) => (
                      <option key={p} value={p} />
                    ))}
                  </datalist>
                  <p className="text-xs text-muted-foreground">
                    Elige uno existente o escribe uno nuevo para crearlo.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notas">Notas (opcional)</Label>
                  <Textarea
                    id="notas"
                    value={form.notas}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    placeholder="Observaciones, faltantes, daños…"
                    rows={3}
                  />
                </div>

                {editingId && (
                  <div className="space-y-2">
                    <Label htmlFor="fecha_recepcion">Fecha y hora de recepción</Label>
                    <Input
                      id="fecha_recepcion"
                      type="datetime-local"
                      value={form.fecha_recepcion}
                      onChange={(e) => setForm({ ...form, fecha_recepcion: e.target.value })}
                    />
                  </div>
                )}

                <div className="space-y-2 rounded-md border p-3 bg-muted/30">
                  <div className="text-xs font-medium">Valoración (opcional)</div>
                  <p className="text-xs text-muted-foreground">
                    Déjalo en blanco si el albarán no está valorado; el administrador lo completará después.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="num_albaran">Nº albarán</Label>
                    <Input
                      id="num_albaran"
                      value={form.numero_albaran}
                      onChange={(e) => setForm({ ...form, numero_albaran: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="importe_total">Importe (€)</Label>
                      <Input
                        id="importe_total" type="number" step="0.01" min="0"
                        value={form.importe_total}
                        onChange={(e) => setForm({ ...form, importe_total: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="iva">% IVA</Label>
                      <Input
                        id="iva" type="number" step="0.01" min="0"
                        value={form.iva_porcentaje}
                        onChange={(e) => setForm({ ...form, iva_porcentaje: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={handleCloseForm}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreate} disabled={creating}>
                    {creating ? 'Guardando…' : (editingId ? 'Guardar cambios' : 'Crear y añadir fotos')}
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-3">
                <RecepcionMaterialDocumentos recepcionId={createdId} tipo="albaran" />
                <RecepcionMaterialDocumentos recepcionId={createdId} tipo="material" />
                <DialogFooter>
                  <Button onClick={handleCloseForm}>Listo</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Buscar</Label>
              <Input
                placeholder="Proveedor, obra, OT, autor…"
                value={filtroBusqueda}
                onChange={(e) => setFiltroBusqueda(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Ubicación</Label>
              <Select value={filtroUbicacion} onValueChange={setFiltroUbicacion}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="almacen">Almacén</SelectItem>
                  <SelectItem value="obra">Obra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Obra</Label>
              <Select value={filtroObra} onValueChange={setFiltroObra}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {obras.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Listado */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No hay recepciones registradas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((r) => (
            <Card key={r.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    {r.ubicacion === 'almacen' ? (
                      <Warehouse className="h-4 w-4 text-orange-400" />
                    ) : (
                      <MapPin className="h-4 w-4 text-cyan-400" />
                    )}
                    {r.proveedor || 'Sin proveedor'}
                  </CardTitle>
                  <Badge variant="secondary" className="capitalize">{r.ubicacion}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {r.obra?.nombre && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Package className="h-3.5 w-3.5" />
                    <span className="truncate">Obra: {r.obra.nombre}</span>
                  </div>
                )}
                {r.orden_trabajo?.numero_ot && (
                  <div className="text-muted-foreground truncate">
                    OT: {r.orden_trabajo.numero_ot} — {r.orden_trabajo.titulo}
                  </div>
                )}
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  {format(new Date(r.fecha_recepcion), "d 'de' MMM yyyy, HH:mm", { locale: es })}
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <User className="h-3.5 w-3.5" />
                  {r.usuario?.nombre || '—'}
                </div>
                {r.notas && (
                  <p className="text-xs line-clamp-2 mt-1">{r.notas}</p>
                )}
                <div className="flex items-center justify-between pt-2">
                  <Badge variant="outline">{r.doc_count || 0} fotos</Badge>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setDetalleId(r.id)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    {(r.usuario_id === user?.id || isOffice) && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {(r.usuario_id === user?.id || isOffice) && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar recepción?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se eliminarán también todas las fotos asociadas. Esta acción no se puede deshacer.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(r)}
                              className="bg-destructive text-destructive-foreground"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detalle */}
      <Dialog open={!!detalleId} onOpenChange={(o) => !o && setDetalleId(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detalle?.ubicacion === 'almacen' ? (
                <Warehouse className="h-5 w-5 text-orange-400" />
              ) : (
                <MapPin className="h-5 w-5 text-cyan-400" />
              )}
              Recepción · {detalle?.proveedor || 'Sin proveedor'}
            </DialogTitle>
          </DialogHeader>
          {detalle && (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-4 grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Ubicación:</span> <span className="capitalize">{detalle.ubicacion}</span></div>
                  <div><span className="text-muted-foreground">Fecha:</span> {format(new Date(detalle.fecha_recepcion), "d MMM yyyy HH:mm", { locale: es })}</div>
                  <div><span className="text-muted-foreground">Subido por:</span> {detalle.usuario?.nombre || '—'}</div>
                  <div><span className="text-muted-foreground">Proveedor:</span> {detalle.proveedor || '—'}</div>
                  {detalle.obra?.nombre && (
                    <div className="col-span-2"><span className="text-muted-foreground">Obra:</span> {detalle.obra.nombre}</div>
                  )}
                  {detalle.orden_trabajo?.numero_ot && (
                    <div className="col-span-2"><span className="text-muted-foreground">OT:</span> {detalle.orden_trabajo.numero_ot} — {detalle.orden_trabajo.titulo}</div>
                  )}
                  {detalle.notas && (
                    <div className="col-span-2"><span className="text-muted-foreground">Notas:</span> {detalle.notas}</div>
                  )}
                </CardContent>
              </Card>

              <RecepcionMaterialDocumentos
                recepcionId={detalle.id}
                tipo="albaran"
                readonly={detalle.usuario_id !== user?.id && !isOffice}
              />
              <RecepcionMaterialDocumentos
                recepcionId={detalle.id}
                tipo="material"
                readonly={detalle.usuario_id !== user?.id && !isOffice}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
